import { useContext, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { BaseContext } from "../base-context";
import { Id } from "../../convex/_generated/dataModel";

const IDLE_THRESHOLD_MS = 120_000; // 2 minutes
const FLUSH_INTERVAL_MS = 30_000;  // 30 seconds

interface AnalyticsEvent {
  type: string;
  route?: string;
  timestamp: number;
  duration_ms?: number;
  active_ms?: number;
  idle_ms?: number;
  referrer_route?: string;
}

function getOrCreateSessionId(): string {
  const key = "analyticsSessionId";
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

/**
 * Tracks user screen time, idle/active state, and page views.
 * Batches events and flushes to Convex every 30 seconds.
 * Should be called once in the Main layout component.
 */
export function useAnalyticsTracker() {
  const { convexUserId, isAdmin } = useContext(BaseContext);
  const location = useLocation();
  const flushEvents = useMutation(api.analytics.flushEvents);

  // Mutable refs (no re-renders)
  const eventBuffer = useRef<AnalyticsEvent[]>([]);
  const currentRoute = useRef(location.pathname);
  const routeEnteredAt = useRef(Date.now());
  const isIdle = useRef(false);
  const idleStartedAt = useRef<number | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout>>();
  const accumulatedIdleMs = useRef(0);
  const sessionId = useRef(getOrCreateSessionId());
  const userIdRef = useRef(convexUserId);

  // Keep userIdRef in sync
  useEffect(() => {
    userIdRef.current = convexUserId;
  }, [convexUserId]);

  // Finalize the current page view and push an event
  const finalizePageView = useCallback((now: number) => {
    const duration = now - routeEnteredAt.current;
    if (duration < 500) return; // ignore sub-500ms visits (instant redirects)

    const idleMs = accumulatedIdleMs.current + (isIdle.current && idleStartedAt.current ? now - idleStartedAt.current : 0);
    const activeMs = Math.max(0, duration - idleMs);

    eventBuffer.current.push({
      type: "page_view",
      route: currentRoute.current,
      timestamp: routeEnteredAt.current,
      duration_ms: duration,
      active_ms: activeMs,
      idle_ms: idleMs,
    });
  }, []);

  // Flush buffered events to Convex
  const flush = useCallback(() => {
    const userId = userIdRef.current;
    if (!userId || eventBuffer.current.length === 0) return;

    const events = [...eventBuffer.current];
    eventBuffer.current = [];

    flushEvents({
      user_id: userId as Id<"users">,
      session_id: sessionId.current,
      events,
    }).catch(() => {
      // Re-add events on failure so they retry next flush
      eventBuffer.current.unshift(...events);
    });
  }, [flushEvents]);

  // Route change tracking
  useEffect(() => {
    if (isAdmin) return; // Don't track admins
    const now = Date.now();
    const prevRoute = currentRoute.current;

    if (location.pathname !== prevRoute) {
      // Finalize previous page view
      finalizePageView(now);

      // Reset for new route
      currentRoute.current = location.pathname;
      routeEnteredAt.current = now;
      accumulatedIdleMs.current = 0;
      isIdle.current = false;
      idleStartedAt.current = null;
    }
  }, [location.pathname, finalizePageView, isAdmin]);

  // Idle detection + periodic flush + visibility/beforeunload
  useEffect(() => {
    if (!convexUserId || isAdmin) return; // Don't track admins

    const resetIdleTimer = () => {
      // If was idle, accumulate that idle time
      if (isIdle.current && idleStartedAt.current) {
        accumulatedIdleMs.current += Date.now() - idleStartedAt.current;
        isIdle.current = false;
        idleStartedAt.current = null;
      }

      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        isIdle.current = true;
        idleStartedAt.current = Date.now();
      }, IDLE_THRESHOLD_MS);
    };

    // Start idle timer
    resetIdleTimer();

    // Activity listeners (passive for performance)
    const activityEvents = ["mousemove", "keydown", "scroll", "touchstart", "click"];
    for (const evt of activityEvents) {
      document.addEventListener(evt, resetIdleTimer, { passive: true });
    }

    // Periodic flush
    const flushInterval = setInterval(() => {
      // Finalize current page view snapshot and immediately start a new measurement
      const now = Date.now();
      finalizePageView(now);
      routeEnteredAt.current = now;
      accumulatedIdleMs.current = 0;
      if (isIdle.current) {
        idleStartedAt.current = now;
      }
      flush();
    }, FLUSH_INTERVAL_MS);

    // Flush on tab hide / beforeunload
    const onVisChange = () => {
      if (document.visibilityState === "hidden") {
        finalizePageView(Date.now());
        flush();
      } else {
        // Tab returned - reset idle tracking for new measurement period
        routeEnteredAt.current = Date.now();
        accumulatedIdleMs.current = 0;
        isIdle.current = false;
        idleStartedAt.current = null;
        resetIdleTimer();
      }
    };
    const onBeforeUnload = () => {
      finalizePageView(Date.now());
      flush();
    };

    document.addEventListener("visibilitychange", onVisChange);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      clearTimeout(idleTimer.current);
      clearInterval(flushInterval);
      for (const evt of activityEvents) {
        document.removeEventListener(evt, resetIdleTimer);
      }
      document.removeEventListener("visibilitychange", onVisChange);
      window.removeEventListener("beforeunload", onBeforeUnload);
      // Flush remaining events on unmount
      finalizePageView(Date.now());
      flush();
    };
  }, [convexUserId, isAdmin, flush, finalizePageView]);
}
